import axios from 'axios';
import useSWR from 'swr';
import imageCompression from 'browser-image-compression';

import { getApiUrl, formatAndThrowApiError } from '@/helpers/api';

export const usePostDocument = () => {
  const postDocument = async (payload) => {
    try {
      const url = `${getApiUrl()}/mega/document`;
      const response = await axios.post(url, payload);
      return response.data;
    } catch (err) {
      formatAndThrowApiError(err);
    }
  };

  return postDocument;
};

export const useDocuments = (params) => {
  const urlStr = `${getApiUrl()}/mega/document${
    params ? `?${new URLSearchParams(params).toString()}` : ''
  }`;
  const swr = useSWR(
    urlStr,
    async (url) => {
      try {
        const response = await axios.get(url);
        return response.data;
      } catch (err) {
        formatAndThrowApiError(err);
      }
    },
    {
      dedupingInterval: 60000, // 60 seconds
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );
  return swr;
};

export const useUploadDocument = () => {
  const uploadDocument = async (filePath, fileType, file) => {
    // Get presigned url
    const response = await axios.post(
      `${getApiUrl()}/file/generate-presigned-url?type=document`,
      {
        fileName: filePath,
        fileType
      }
    );
    const presignedUrl = response.data.url;

    // Extract the path from the presigned URL
    const urlObject = new URL(presignedUrl);
    const documentPath = `${urlObject.origin}${urlObject.pathname}`;

    // Upload File
    await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': fileType
      }
    });

    return documentPath;
  };

  return uploadDocument;
};

export const useDocumentPreview = () => {
  const getDocumentPreview = async (document) => {
    try {
      // Get download signature
      const url = `${getApiUrl()}/mega/document/${
        document.id
      }/download-signature`;
      const signatureResponse = await axios.get(url);
      const signedUrl = signatureResponse.data.signedUrl;

      // Download the actual file
      const fileResponse = await axios.get(signedUrl, {
        responseType: 'blob',
        withCredentials: false
      });

      const contentType = fileResponse.headers['content-type'];

      if (contentType.startsWith('image/')) {
        // For images, resize using browser-image-compression
        const options = {
          maxSizeMB: 2,
          maxWidthOrHeight: 1000,
          useWebWorker: true
        };

        const compressedFile = await imageCompression(
          fileResponse.data,
          options
        );
        return window.URL.createObjectURL(compressedFile);
      } else {
        // For non-image files, return the signed URL directly
        return signedUrl;
      }
    } catch (err) {
      // console.error(err);
      formatAndThrowApiError(err);
    }
  };

  return getDocumentPreview;
};
